import { makeAutoObservable } from "mobx";
import type { ShellPreferenceCache } from "./preferences/shell-preference-cache";
import { browserShellPreferenceCache } from "./preferences/shell-preference-cache";

export const themeCodes = ["light", "dark-blue", "grey", "high-contrast"] as const;
export type ThemeCode = (typeof themeCodes)[number];

function isThemeCode(value: string | undefined): value is ThemeCode {
  return themeCodes.some((theme) => theme === value);
}

/**
 * The theme a first-time visitor gets, before they have chosen one.
 *
 * `dark-blue` regardless of `prefers-color-scheme`, because it is the approved
 * design: the redesign is a dark palette, and its warm selection accent only
 * exists on a dark surface. On a light page an amber bright enough to read as
 * the mockup's accent cannot also hold 3:1 against white — measured, not
 * assumed — so the light theme necessarily looks quieter and an operator who
 * had never opened the theme menu was seeing a muted translation of the design
 * rather than the design.
 *
 * A stored choice always wins (see the constructor), so this only decides the
 * very first load, and light/grey/high-contrast stay one click away in the
 * header's theme menu.
 */
function preferredTheme(): ThemeCode {
  return "dark-blue";
}

export class ThemeStore {
  theme: ThemeCode;

  constructor(
    initialTheme?: ThemeCode,
    private readonly cache: ShellPreferenceCache = browserShellPreferenceCache,
  ) {
    const cached = cache.read("theme");
    this.theme = initialTheme ?? (isThemeCode(cached) ? cached : preferredTheme());
    makeAutoObservable(this, {}, { autoBind: true });
  }

  setTheme(theme: ThemeCode): void {
    this.theme = theme;
    this.cache.write("theme", theme);
    this.apply();
  }

  apply(): void {
    if (typeof document !== "undefined") {
      document.documentElement.dataset.theme = this.theme;
      document.documentElement.style.colorScheme =
        this.theme === "light" || this.theme === "grey" ? "light" : "dark";
    }
  }
}
